import { useQuery } from '@tanstack/react-query';

import { MintReadiness, getMintReadiness } from '../api/waves';
import { deviceCodeBlockState } from '../lib/deviceCodeBlocking';
import { errorText } from '../lib/errorText';
import { STATUS, STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-451 — the FD-ADR-021 minting account's prerequisites, on the activate
// dialog, BEFORE the operator signs in.
//
// The problem it solves is timing, not capability. `Get-BprtServiceAccountHealth`
// has probed presence, role and CA exclusion since FD-439, and the Tenants page
// has shown the verdict — but on the hourly refresh, on a different screen, and
// not at the moment an operator is about to start an interactive sign-in. So a
// missing role surfaced as `AADSTS240005` partway through that sign-in, which is
// exactly the opaque-error class FD-ADR-032 decided should be named.
//
// ── FETCH POLICY: the LicenseBalancePanel rules, for the same reason ────────
// Dialog-open only. Never on the ~5 s `useDeviceCodeActivation` poll, never on
// focus, never on reconnect. These are real Graph reads against a customer
// tenant; putting them on the activation loop would be ~180 per activation.
// Every option is set EXPLICITLY so a future react-query default cannot quietly
// turn this into a polled read.
export function useMintReadiness(migrationId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['mint-readiness', migrationId],
    queryFn: () => getMintReadiness(migrationId),
    enabled,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    // Zero so REOPENING re-reads. An operator who just fixed a role assignment
    // and reopened the dialog must not be shown the pre-fix verdict.
    staleTime: 0,
    // No retry: the panel is guidance and activation proceeds without it, so a
    // retry would only make the dialog slower for someone it never blocks.
    retry: false,
  });
}

const box: React.CSSProperties = {
  border: `1px solid ${STROKE.base}`,
  background: SURFACE.rowFill,
  borderRadius: 6,
  padding: '10px 12px',
};

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
      <span style={{ color: TEXT.secondary }}>{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

/**
 * FD-592 (FD-ADR-047 §2) — the named device-code probe, one line. Four states, and
 * a Manager that predates FD-591 (`undefined`) renders NO line rather than an
 * "unknown" it never asked about.
 */
function DeviceCodeLine({ readiness }: { readiness: MintReadiness }) {
  const state = deviceCodeBlockState(readiness);
  if (state.kind === 'not-asked') return null;
  let text: string;
  let color: string = TEXT.secondary;
  if (state.kind === 'unknown') {
    text = 'could not check';
  } else if (state.kind === 'clear') {
    text = 'no blocking policy found';
    color = STATUS.successText;
  } else if (state.covers.length > 0) {
    text = `blocked by ${state.covers.map((p) => p.displayName).join(', ')}`;
    color = STATUS.warningText;
  } else if (state.mayCover.length > 0) {
    text = `may be blocked by ${state.mayCover.map((p) => p.displayName).join(', ')}`;
    color = STATUS.warningText;
  } else {
    text = `report-only: ${state.reportOnly.map((p) => p.displayName).join(', ')}`;
  }
  return (
    <Line label="Device-code sign-in">
      <span style={{ color }} data-testid="device-code-line">
        {text}
      </span>
    </Line>
  );
}

export interface MintReadinessPanelProps {
  readiness: MintReadiness | null | undefined;
  isLoading: boolean;
  error: unknown;
}

export default function MintReadinessPanel({ readiness, isLoading, error }: MintReadinessPanelProps) {
  if (isLoading) {
    return (
      <div style={box} className="font-display text-[12.5px]" data-testid="mint-readiness">
        <span style={{ color: TEXT.secondary }}>Checking the minting account…</span>
      </div>
    );
  }

  // A failed check is NOT a failed activation. It says so, and gets out of the way.
  if (error || !readiness) {
    return (
      <div style={box} className="font-display text-[12.5px]" data-testid="mint-readiness">
        <span style={{ color: TEXT.secondary }}>
          Could not check the minting account ({errorText(error)}). This does not block activation —
          continue, and the sign-in itself will report any problem.
        </span>
      </div>
    );
  }

  const { account, checked, checkReason, serviceAccountUpn, serviceAccountSource, tenantDomain } = readiness;

  // "Not configured" is a distinct, actionable state — never rendered as a
  // problem with the account, because there is no account to have a problem.
  const nothingResolved = serviceAccountSource === 'none' || !serviceAccountUpn;

  // Optimistic by design (FD-442): it can miss a problem, it will not invent
  // one. So only a POSITIVE failed signal is drawn as a failure; everything
  // unknown is drawn as unknown.
  const failed = checked && account ? !account.present || !account.role || account.caExclusionHealthy === false : false;

  const tone = nothingResolved || failed ? STATUS.warning : checked ? STATUS.success : TEXT.secondary;

  return (
    <div style={box} className="flex flex-col gap-1.5 font-display" data-testid="mint-readiness">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[12.5px] font-medium">Minting account</span>
        <span className="font-mono text-[11px]" style={{ color: tone }}>
          {nothingResolved ? 'not configured' : failed ? 'needs attention' : checked ? 'checked' : 'not checked'}
        </span>
      </div>

      {nothingResolved ? (
        <span className="text-[12.5px]" style={{ color: STATUS.warning }}>
          No account is recorded for {tenantDomain ?? 'this destination tenant'}. Set it on Tenants ›
          Service accounts — signing in as the wrong account is <code>AADSTS240005</code>.
        </span>
      ) : (
        <>
          <Line label="Sign in as">
            <span className="font-mono text-[11.5px]">{serviceAccountUpn}</span>
          </Line>
          <Line label="Source">
            {/* FD-450: a deployment-wide default and a per-tenant value looked
                identical here while meaning entirely different things. */}
            <span style={{ color: TEXT.secondary }} className="text-[11.5px]">
              {serviceAccountSource === 'tenant'
                ? `recorded for ${tenantDomain ?? 'this tenant'}`
                : 'deployment default'}
            </span>
          </Line>
          {checked && account ? (
            <>
              <Line label="Account exists">
                <span style={{ color: account.present ? STATUS.success : STATUS.warning }}>
                  {account.present ? 'yes' : 'not found'}
                </span>
              </Line>
              <Line label="Directory role">
                <span style={{ color: account.role ? STATUS.success : STATUS.warning }}>
                  {account.role ?? 'none — the mint will fail with AADSTS240005'}
                </span>
              </Line>
              <Line label="Conditional Access">
                {/* FD-609: three states. null/undefined is "could not verify",
                    drawn as unknown — never as a problem the probe did not find. */}
                <span
                  style={{
                    color:
                      account.caExclusionHealthy === true
                        ? STATUS.success
                        : account.caExclusionHealthy === false
                          ? STATUS.warning
                          : TEXT.secondary,
                  }}
                >
                  {account.caExclusionHealthy === true
                    ? 'no blocking policy seen'
                    : account.caExclusionHealthy === false
                      ? 'a policy may block the sign-in'
                      : 'could not verify the Conditional Access exclusion'}
                </span>
              </Line>
              <DeviceCodeLine readiness={readiness} />
            </>
          ) : (
            <span className="text-[12.5px]" style={{ color: TEXT.secondary }}>
              {checkReason ?? 'The account could not be checked.'}
            </span>
          )}
        </>
      )}

      {/* The caveats travel with the verdict. A green line here is not proof,
          and saying so is what keeps the panel honest rather than reassuring. */}
      {checked && (
        <ul className="mt-0.5 flex list-disc flex-col gap-0.5 pl-4 text-[11px]" style={{ color: TEXT.secondary }}>
          {readiness.caveats.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
